/** Cambio de clave propio: actual, nueva y confirmacion (design.md §22.2 y §22.7).
 *
 * Tras el cambio el backend cierra la sesion, asi que el hook vacia la
 * cache y vuelve al login con el aviso `?passwordChanged=true`.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput } from '@/components/MobileInput';
import { SectionCard } from '@/components/SectionCard';
import { useChangePasswordMutation } from '@/hooks/useMobilePassword';
import {
  mobileChangePasswordSchema,
  type MobileChangePasswordForm,
} from '@/schemas/password.schemas';
import { extractErrorMessage } from '@/services/mobileApiClient';

export function ChangePasswordPage() {
  const changeMutation = useChangePasswordMutation();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MobileChangePasswordForm>({
    resolver: yupResolver(mobileChangePasswordSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  async function onSubmit(values: MobileChangePasswordForm) {
    setGeneralError(null);
    try {
      await changeMutation.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
    } catch (error) {
      setGeneralError(extractErrorMessage(error));
    }
  }

  const busy = isSubmitting || changeMutation.isPending;

  function visibilityToggle(
    visible: boolean,
    onToggle: () => void,
    label: string,
  ) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-bg-tertiary"
        aria-label={label}
        aria-pressed={visible}
      >
        <MobileIcon
          name={visible ? 'visibility_off' : 'visibility'}
          className="h-5 w-5 text-text-secondary"
        />
      </button>
    );
  }

  return (
    <MobileLayout title="Cambia tu contraseña" subtitle="Actualiza tu clave de acceso" showBack>
      <div className="flex justify-center pb-6 pt-2">
        <img
          src="/rpa-value-logo.png"
          alt="RPA Value"
          className="h-auto w-[160px]"
        />
      </div>
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
        <SectionCard title="Tu acceso">
          <div className="space-y-4">
            <MobileInput
              label="Clave actual"
              type={showCurrent ? 'text' : 'password'}
              autoComplete="current-password"
              enterKeyHint="next"
              placeholder="••••••••"
              staticLabel
              isRequired
              icon={<MobileIcon name="lock" className="h-5 w-5" />}
              iconRight={visibilityToggle(
                showCurrent,
                () => setShowCurrent((v) => !v),
                showCurrent ? 'Ocultar clave actual' : 'Mostrar clave actual',
              )}
              error={errors.current_password?.message}
              {...register('current_password')}
            />
            <MobileInput
              label="Nueva clave"
              type={showNew ? 'text' : 'password'}
              autoComplete="new-password"
              enterKeyHint="next"
              placeholder="Mínimo 10 caracteres"
              staticLabel
              isRequired
              icon={<MobileIcon name="lock" className="h-5 w-5" />}
              iconRight={visibilityToggle(
                showNew,
                () => setShowNew((v) => !v),
                showNew ? 'Ocultar nueva clave' : 'Mostrar nueva clave',
              )}
              help="Mínimo 10 caracteres, combina 3 de 4 clases: minúsculas, mayúsculas, dígitos, símbolos."
              error={errors.new_password?.message}
              {...register('new_password')}
            />
            <MobileInput
              label="Confirmar nueva clave"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              enterKeyHint="go"
              placeholder="Repite la nueva clave"
              staticLabel
              isRequired
              icon={<MobileIcon name="lock" className="h-5 w-5" />}
              iconRight={visibilityToggle(
                showConfirm,
                () => setShowConfirm((v) => !v),
                showConfirm ? 'Ocultar confirmación' : 'Mostrar confirmación',
              )}
              error={errors.confirm_password?.message}
              {...register('confirm_password')}
            />
          </div>
        </SectionCard>

        {generalError && (
          <div className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{generalError}</p>
          </div>
        )}

        <MobileButton
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={busy}
          icon={<MobileIcon name="save" className="h-5 w-5" />}
        >
          Actualizar contraseña
        </MobileButton>
      </form>
    </MobileLayout>
  );
}
